// Domain-aware editions. Same codebase/backend/DB.
import axios from "axios";

// - alumnipulse.net  -> global edition (all countries, India included)
// - indiaalumni.com  -> India edition (India-first content, own branding)
// Preview override: append ?edition=india or ?edition=global (persists in localStorage).

const INDIA_HOSTS = ["indiaalumni.com", "www.indiaalumni.com", "indiaalumni.net", "www.indiaalumni.net"];
const EDU_HOSTS = ["indianedupulse.com", "www.indianedupulse.com", "indiaedupulse.com", "www.indiaedupulse.com"];

function computeEditionId() {
  // Deployment-level override — used by a dedicated deployment/fork
  // (set REACT_APP_EDITION in that deployment's frontend env).
  const forced = (process.env.REACT_APP_EDITION || "").toLowerCase();
  if (forced === "india") return "india";
  if (forced === "global") return "global";
  if (forced === "edupulse") return "edupulse";

  // Path-based entry: /india (used with registrar forwarding indiaalumni.com -> /india).
  try {
    const path = (window.location.pathname || "").toLowerCase();
    if (path === "/india" || path.startsWith("/india/")) {
      localStorage.setItem("ap_edition", "india");
      return "india";
    }
  } catch (_) {}

  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("edition");
    if (q === "india" || q === "global" || q === "edupulse") localStorage.setItem("ap_edition", q);
  } catch (_) {}

  const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
  if (EDU_HOSTS.some((h) => host === h || host.endsWith("." + h))) return "edupulse";
  if (INDIA_HOSTS.some((h) => host === h || host.endsWith("." + h))) return "india";

  let stored = null;
  try { stored = localStorage.getItem("ap_edition"); } catch (_) {}
  if (stored === "india" || stored === "global" || stored === "edupulse") return stored;

  return "global";
}

const GLOBAL = {
  id: "global",
  brand: "AlumniPulse",
  mark: "™",
  domain: "alumnipulse.net",
  url: "https://alumnipulse.net",
  logo: "/alumnipulse-logo.png",
  country: null, // no scoping
  copyright: "© 2026 AlumniPulse.net — Social Pulse Inc",
  other: { brand: "IndiaAlumni", url: "https://indiaalumni.com", label: "IndiaAlumni.com", note: "Indian alumni? Visit" },
  emails: {
    noreply: "noreply@alumnipulse.net",
    contact: "contact@alumnipulse.net",
    info: "info@alumnipulse.net",
    privacy: "privacy@alumnipulse.net",
    abuse: "abuse@alumnipulse.net",
  },
};

const INDIA = {
  id: "india",
  brand: "IndiaAlumni",
  mark: "™",
  domain: "indiaalumni.com",
  url: "https://indiaalumni.com",
  logo: "/indiaalumni-logo.png",
  country: "India", // scope public browsing to India
  copyright: "© 2026 IndiaAlumni.com — Social Pulse Inc",
  other: { brand: "AlumniPulse", url: "https://alumnipulse.net", label: "AlumniPulse.net", note: "Looking for alumni outside India? Visit" },
  emails: {
    noreply: "noreply@indiaalumni.com",
    support: "support@indiaalumni.com",
    contact: "contact@indiaalumni.com",
    info: "info@indiaalumni.com",
    privacy: "legal@indiaalumni.com",
    legal: "legal@indiaalumni.com",
    admin: "admin@indiaalumni.com",
    abuse: "abuse@indiaalumni.com",
  },
};

const EDUPULSE = {
  id: "edupulse",
  brand: "IndianEduPulse",
  mark: "™",
  domain: "indianedupulse.com",
  url: "https://indianedupulse.com",
  logo: "/indianedupulse-logo.png",
  country: "India", // education discovery scoped to India
  copyright: "© 2026 IndianEduPulse.com — Social Pulse Inc",
  other: { brand: "IndiaAlumni", url: "https://indiaalumni.com", label: "IndiaAlumni.com", note: "Reconnect with alumni on" },
  emails: {
    noreply: "noreply@indianedupulse.com",
    support: "support@indianedupulse.com",
    contact: "contact@indianedupulse.com",
    info: "info@indianedupulse.com",
    privacy: "legal@indianedupulse.com",
    legal: "legal@indianedupulse.com",
    admin: "admin@indianedupulse.com",
    abuse: "abuse@indianedupulse.com",
  },
};

export const EDITION_ID = computeEditionId();
export const EDITIONS = { global: GLOBAL, india: INDIA, edupulse: EDUPULSE };
export const EDITION = EDITIONS[EDITION_ID] || GLOBAL;
export const isIndia = EDITION_ID === "india";
export const isEdu = EDITION_ID === "edupulse";
// India-scoped editions (IndiaAlumni + IndianEduPulse both browse only India).
export const isIndiaScoped = isIndia || isEdu;

// Append the India country scope to a query string when in the India edition.
export function scopedPath(path, extraParams = {}) {
  const params = new URLSearchParams(extraParams);
  if (EDITION.country) params.set("country", EDITION.country);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export function useEdition() {
  return EDITION;
}

// Set document title + favicon for the active edition (preview + live).
try {
  if (typeof document !== "undefined" && isIndia) {
    document.title = "IndiaAlumni — Reconnect With Your Past. Build Your Future. Leave Your Legacy.";
    const link = document.querySelector("link[rel~='icon']");
    if (link) link.href = INDIA.logo;
  }
  if (typeof document !== "undefined" && isEdu) {
    document.title = "IndianEduPulse — Discover India's Education. Connect with Its People. Preserve Its History.";
    const link = document.querySelector("link[rel~='icon']");
    if (link) link.href = EDUPULSE.logo;
  }
  if (isIndia) axios.defaults.headers.common["X-Edition"] = "india";
  if (isEdu) axios.defaults.headers.common["X-Edition"] = "edupulse";
} catch (_) {}
